import { useContext, useState } from 'react';
import { Redirect } from 'react-router';

import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { DataProxy } from '@apollo/client/cache';
import { Box, Button, Flex, H4, Loader, Text } from '@opendoor/bricks/core';
import ContentCard from '@opendoor/bricks/deprecated/UNSAFE_layout/ContentCard';
import { spaceWithUnits } from '@opendoor/bricks/theme/eero';

import { ContentWrapper } from '../../components/ContentWrapper';
import TestModeBanner from '../../components/TestModeBanner';
import {
  UNSAFE_ISingleLineInputProps, // eslint-disable-next-line import/no-deprecated
  UNSAFE_SingleLineInput,
} from '../../components/UNSAFE_SingleLineInput';
import { productionClient, testingClient } from '../../config/apolloClients';
import { TestModeContext } from '../../config/context';
import {
  CREATE_WEBHOOK_URL,
  ICreateOrUpdateWebhookUrl,
  ICreateOrUpdateWebhookUrlVariables,
} from '../../graphql/mutations/CreateOrUpdateWebhookUrl';
import { DELETE_WEBHOOK_URL, IDeleteWebhookUrl } from '../../graphql/mutations/DeleteWebhookUrl';
import { GET_WEBHOOK_URL, IGetWebhookUrl } from '../../graphql/queries/GetWebhookUrl';
import { getErrorMessage } from '../../helpers/formatUtils';
import { ERROR_PAGE_ROUTE } from '../routes';

const Webhooks = () => {
  const testMode = useContext(TestModeContext);

  const [webhookValue, setWebhookValue] = useState(''); // For changing the content of the Bricks Input
  const [gotDataFlag, setGotDataFlag] = useState(false); // Avoid updating content with query data in every render
  const [successMessage, setSuccessMessage] = useState('');

  /*
   * Callback for the create/update mutation hook to update the cached query response with the saved webhook data
   */
  const handleWebhookUpdate = (
    cache: DataProxy,
    { data }: FetchResult<ICreateOrUpdateWebhookUrl>,
  ) => {
    if (data?.createOrUpdateWebhookUrl.success) {
      cache.writeQuery({
        query: GET_WEBHOOK_URL,
        data: {
          webhookUrl: data.createOrUpdateWebhookUrl.webhookUrl,
        },
      });
      setSuccessMessage('Your webhook endpoint has been saved.');
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    }
  };

  const handleWebhookDelete = (cache: DataProxy, { data }: FetchResult<IDeleteWebhookUrl>) => {
    if (data?.deleteWebhookUrl) {
      cache.writeQuery({
        query: GET_WEBHOOK_URL,
        data: {
          webhookUrl: null,
        },
      });
      setWebhookValue('');
      setSuccessMessage('Your webhook endpoint has been deleted.');
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    }
  };

  const { loading, error, data } = useQuery<IGetWebhookUrl>(GET_WEBHOOK_URL, {
    client: testMode ? testingClient : productionClient,
  });

  const [addWebhook, { loading: addWebhookLoading, error: addWebhookError }] = useMutation<
    ICreateOrUpdateWebhookUrl,
    ICreateOrUpdateWebhookUrlVariables
  >(CREATE_WEBHOOK_URL, {
    client: testMode ? testingClient : productionClient,
    update: handleWebhookUpdate,
  });

  const [deleteWebhook, { loading: deleteWebhookLoading, error: deleteWebhookError }] =
    useMutation<IDeleteWebhookUrl>(DELETE_WEBHOOK_URL, {
      client: testMode ? testingClient : productionClient,
      update: handleWebhookDelete,
    });

  // If there is an error from the get, then we have to hard fail
  if (error) {
    return <Redirect to={ERROR_PAGE_ROUTE} />;
  }

  // We only want to override the input content if it's from the initial data fetch
  if (data && data.webhookUrl && !gotDataFlag) {
    setWebhookValue(data.webhookUrl);
    setGotDataFlag(true);
  }

  const handleSave = () => {
    addWebhook({ variables: { webhookUrl: webhookValue } });
  };

  const handleDelete = () => {
    deleteWebhook();
  };

  const handleChange: UNSAFE_ISingleLineInputProps['onChange'] = (_, value) => {
    setWebhookValue(value ? value : '');
  };

  return (
    <ContentWrapper>
      <ContentCard
        title="Webhooks"
        borderRadius="roundedSquare"
        paddingTop={testMode ? spaceWithUnits[10] : undefined}
      >
        {testMode ? <TestModeBanner /> : null}
        <Box mt={3}>
          Use webhooks to get notified about events that are relevant to your application. For now,
          we only support state transition events for offers initiated from your application.
        </Box>
        <Box mt={2}>
          <a
            target="_blank"
            href="https://docs.opendoor.com/api-reference/receive-status-updates"
            rel="noopener noreferrer"
          >
            Learn more →
          </a>
        </Box>
        <Box mt={7}>
          <H4 color="neutrals100" mb={2}>
            {testMode ? 'Testing Endpoint' : 'Endpoint'}
          </H4>
          {loading ? (
            <Box>
              <Loader />
            </Box>
          ) : (
            <Flex>
              <Box width="400px" mr={2} display="flex" alignItems="center">
                {/* eslint-disable-next-line react/jsx-pascal-case */}
                <UNSAFE_SingleLineInput
                  name="webhook-input"
                  assertNotNull={false}
                  content={webhookValue}
                  unboundedWidth
                  onChange={handleChange}
                  hasError={!!addWebhookError}
                  placeholder={'e.g. https://your-app.com/api/webhook'}
                />
              </Box>
              <Box display="inline-block" ml={4}>
                <Button
                  size="md"
                  onClick={handleSave}
                  loading={addWebhookLoading}
                  loadingText={'Save'}
                  variant="primary"
                  analyticsName="partnership-dashboard-fe-save-webhook"
                  aria-label="Save Webhook"
                >
                  Save
                </Button>
              </Box>
              {data && data.webhookUrl && (
                <Box display="inline-block" ml={3}>
                  <Button
                    size="md"
                    onClick={handleDelete}
                    loading={deleteWebhookLoading}
                    loadingText={'Save'}
                    variant="secondary"
                    analyticsName="partnership-dashboard-fe-delete-webhook"
                    aria-label="Delete Webhook"
                  >
                    Delete
                  </Button>
                </Box>
              )}
            </Flex>
          )}
          <Box height="20px">
            {addWebhookError && (
              <Text color="terracotta50">{getErrorMessage(addWebhookError)}</Text>
            )}
            {deleteWebhookError && (
              <Text color="terracotta50">{getErrorMessage(deleteWebhookError)}</Text>
            )}
            {successMessage && <Text color="green50">{successMessage}</Text>}
          </Box>
        </Box>
      </ContentCard>
    </ContentWrapper>
  );
};

export default Webhooks;
